import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpContext } from '@angular/common/http';
import { Observable } from 'rxjs';
import { NO_LOADER } from '../interceptors/interceptor';

export interface HttpOptions {
  headers?: HttpHeaders;
  context?: HttpContext;
  params?: HttpParams;
  noLoader?: boolean;
  responseType?: 'json' | 'blob' | 'text' | 'arraybuffer';
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  constructor(private http: HttpClient) {}

  /**
   * Builds HttpParams from an object, filtering out null and undefined values
   */
  buildParams<T extends Record<string, any>>(paramsObject?: T): HttpParams {
    let params = new HttpParams();
    if (paramsObject) {
      Object.keys(paramsObject).forEach((key) => {
        const value = paramsObject[key as keyof T];
        if (value !== undefined && value !== null) {
          params = params.append(key, value.toString());
        }
      });
    }
    return params;
  }

  private buildOptions(options: HttpOptions = {}, body?: any): any {
    let headers = options.headers || new HttpHeaders();
    let context = options.context || new HttpContext();

    const isFormData = body instanceof FormData;
    const isBlob = body instanceof Blob;
    const hasCustomContentType = options.headers?.has('Content-Type');

    if (!isFormData && !isBlob && !hasCustomContentType) {
      headers = headers.set('Content-Type', 'application/json; charset=utf-8');
    }

    if (options.noLoader) {
      context = context.set(NO_LOADER, true);
    }

    const { noLoader, ...rest } = options;

    return {
      ...rest,
      headers,
      context,
    };
  }

  get<T>(url: string, options?: HttpOptions): Observable<any> {
    return this.http.get<T>(url, this.buildOptions(options));
  }

  post<T>(url: string, body: any, options?: HttpOptions): Observable<any> {
    return this.http.post<T>(url, body, this.buildOptions(options, body));
  }

  put<T>(url: string, body: any, options?: HttpOptions): Observable<any> {
    return this.http.put<T>(url, body, this.buildOptions(options, body));
  }

  delete<T>(url: string, options?: HttpOptions): Observable<any> {
    return this.http.delete<T>(url, this.buildOptions(options));
  }

  patch<T>(url: string, body: any, options?: HttpOptions): Observable<any> {
    return this.http.patch<T>(url, body, this.buildOptions(options, body));
  }
}
